import CryptoJS from 'crypto-js';
import {
    sharedKey as computeSharedSecret,
    generateKeyPair as genKeyPair,
} from 'curve25519-js';

import { Convert } from '../utils/Convert';
import { HexEncoder } from '../utils/HexEncoder';
import { Security } from './Security';

const Session = require('../proto/session_pb');
const textEncoder = new TextEncoder();

type WordArray = CryptoJS.lib.WordArray;

/**
 * Abstract base cipher template.
 */
interface Cipher {
    /**
     * This cipher's key size. Default: 4 (128 bits)
     */
    keySize: number;
    /**
     * This cipher's IV size. Default: 4 (128 bits)
     */
    ivSize: number;
    /**
     * A constant representing encryption mode.
     */
    readonly _ENC_XFORM_MODE: number;
    /**
     * A constant representing decryption mode.
     */
    readonly _DEV_XFORM_MODE: number;

    /**
     * Resets this cipher to its initial state.
     *
     * @example
     *
     *     cipher.reset();
     */
    reset(): void;

    /**
     * Adds data to be encrypted or decrypted.
     *
     * @param dataUpdate The data to encrypt or decrypt.
     *
     * @return The data after processing.
     *
     * @example
     *
     *     var encrypted = cipher.process('data');
     *     var encrypted = cipher.process(wordArray);
     */
    process(dataUpdate: WordArray | string): WordArray;

    /**
     * Finalizes the encryption or decryption process.
     * Note that the finalize operation is effectively a destructive, read-once operation.
     *
     * @param dataUpdate The final data to encrypt or decrypt.
     *
     * @return The data after final processing.
     *
     * @example
     *
     *     var encrypted = cipher.finalize();
     *     var encrypted = cipher.finalize('data');
     *     var encrypted = cipher.finalize(wordArray);
     */
    finalize(dataUpdate?: WordArray | string): WordArray;
}

/**
 * Security 1 implementation of the handshake and encryption
 * protocols.
 * Security 1 is based on AES CTR mode with NoPadding
 */
export class Security1 implements Security {
    private static readonly SESSION_STATE_REQUEST1 = 0;
    private static readonly SESSION_STATE_RESPONSE1_REQUEST2 = 1;
    private static readonly SESSION_STATE_RESPONSE2 = 2;
    private static readonly SESSION_STATE_FINISHED = 3;

    private sessionState = Security1.SESSION_STATE_REQUEST1;
    private privateKey?: Uint8Array;
    private publicKey?: Uint8Array;
    private proofOfPossession?: Uint8Array;
    private clientVerify?: Uint8Array;

    private cipher?: Cipher; // CryptoJS does not expose the Cipher interface so we use a copy of it

    private deviceRandom?: Uint8Array;
    private secretKey?: Uint8Array;

    private cipherInputs: Uint8Array[] = [];

    constructor(proofOfPossession?: string) {
        if (proofOfPossession) {
            this.proofOfPossession = textEncoder.encode(proofOfPossession);
        }
    }

    public getNextRequestInSession(
        hexData?: Uint8Array
    ): Uint8Array | undefined {
        let request: Uint8Array | undefined;

        switch (this.sessionState) {
            case Security1.SESSION_STATE_REQUEST1:
                this.sessionState = Security1.SESSION_STATE_RESPONSE1_REQUEST2;
                request = this.getStep0Request();
                break;
            case Security1.SESSION_STATE_RESPONSE1_REQUEST2:
                this.sessionState = Security1.SESSION_STATE_RESPONSE2;
                this.processStep0Response(hexData);
                request = this.getStep1Request();
                break;
            case Security1.SESSION_STATE_RESPONSE2:
                this.sessionState = Security1.SESSION_STATE_FINISHED;
                this.processStep1Response(hexData);
                break;
            default:
                break;
        }

        return request;
    }

    private getStep0Request(): Uint8Array | undefined {
        try {
            this.generateKeyPair();
            const sessionCmd0 = new Session.SessionCmd0();
            sessionCmd0.setClientPubkey(this.publicKey);
            const sec1Payload = new Session.Sec1Payload();
            sec1Payload.setSc0(sessionCmd0);
            const sessionData = new Session.SessionData();
            sessionData.setSecVer(Session.SecSchemeVersion.SECSCHEME1);
            sessionData.setSec1(sec1Payload);

            return sessionData.serializeBinary();
        } catch (e) {
            console.error(e);
        }

        return undefined;
    }

    private getStep1Request(): Uint8Array {
        const sessionCmd1 = new Session.SessionCmd1();
        sessionCmd1.setClientVerifyData(this.clientVerify);
        const sec1Payload = new Session.Sec1Payload();
        sec1Payload.setSc1(sessionCmd1);
        sec1Payload.setMsg(Session.Sec1MsgType.SESSION_COMMAND1);
        const sessionData = new Session.SessionData();
        sessionData.setSecVer(Session.SecSchemeVersion.SECSCHEME1);
        sessionData.setSec1(sec1Payload);

        return sessionData.serializeBinary();
    }

    private processStep0Response(hexData?: Uint8Array): void {
        try {
            if (!hexData) {
                throw new Error('No response from device');
            }
            const responseData =
                Session.SessionData.deserializeBinary(hexData);
            if (
                responseData.getSecVer() !==
                Session.SecSchemeVersion.SECSCHEME1
            ) {
                throw new Error('Security version mismatch');
            }

            const devicePublicKey: Uint8Array = responseData
                .getSec1()
                .getSr0()
                .getDevicePubkey();
            const deviceRandom = responseData
                .getSec1()
                .getSr0()
                .getDeviceRandom();
            let sharedKey = computeSharedSecret(
                this.privateKey,
                devicePublicKey
            );

            if (this.proofOfPossession && this.proofOfPossession.length > 0) {
                const hashWordArray = CryptoJS.SHA256(
                    Convert.uint8ArrayToWordArray(this.proofOfPossession)
                );
                const hash = Convert.wordArrayToUint8Array(hashWordArray);
                sharedKey = HexEncoder.xor(sharedKey, hash);
            }

            this.cipher = CryptoJS.algo.AES.createEncryptor(
                Convert.uint8ArrayToWordArray(sharedKey),
                {
                    mode: CryptoJS.mode.CTR,
                    iv: Convert.uint8ArrayToWordArray(deviceRandom),
                    padding: CryptoJS.pad.NoPadding,
                    blockSize: 256,
                }
            );

            this.secretKey = sharedKey;
            this.deviceRandom = deviceRandom;

            this.clientVerify = this.encrypt(devicePublicKey);
        } catch (e) {
            console.error(e);
        }
    }

    private processStep1Response(hexData?: Uint8Array): void {
        try {
            if (!hexData) {
                throw new Error('No response from device');
            }

            const responseData =
                Session.SessionData.deserializeBinary(hexData);
            if (
                responseData.getSecVer() !==
                Session.SecSchemeVersion.SECSCHEME1
            ) {
                throw new Error('Security version mismatch');
            }
            const deviceVerifyData = responseData
                .getSec1()
                .getSr1()
                .getDeviceVerifyData();
            const decryptedDeviceVerifyData = this.decrypt(deviceVerifyData);
            if (
                JSON.stringify(this.publicKey) !==
                JSON.stringify(decryptedDeviceVerifyData)
            ) {
                throw new Error('Session establishment failed !');
            }
        } catch (e) {
            console.log(e);
        }
    }

    private generateKeyPair(): void {
        const keyPairClient = genKeyPair(new Uint8Array(32));
        this.privateKey = keyPairClient.private;
        this.publicKey = keyPairClient.public;
    }

    private process(data: Uint8Array): Uint8Array {
        let lastProcessedInput: WordArray | undefined;

        this.cipherInputs.push(data);

        this.cipher = CryptoJS.algo.AES.createEncryptor(
            Convert.uint8ArrayToWordArray(this.secretKey!),
            {
                mode: CryptoJS.mode.CTR,
                iv: Convert.uint8ArrayToWordArray(this.deviceRandom!),
                padding: CryptoJS.pad.NoPadding,
            }
        );

        this.cipherInputs.forEach((input) => {
            lastProcessedInput = this.cipher?.process(
                Convert.uint8ArrayToWordArray(input)
            );
        });

        const finalData = this.cipher.finalize();
        const lastProcessedDataUint8Array = Convert.wordArrayToUint8Array(
            lastProcessedInput!
        );
        const finalDataUint8Array = Convert.wordArrayToUint8Array(finalData);
        const mergedUint8Array = new Uint8Array(
            lastProcessedDataUint8Array.length + finalDataUint8Array.length
        );
        mergedUint8Array.set(lastProcessedDataUint8Array);
        mergedUint8Array.set(
            finalDataUint8Array,
            lastProcessedDataUint8Array.length
        );
        const slicedMergedUint8Array = mergedUint8Array.slice(-data.length);

        return slicedMergedUint8Array;
    }

    public encrypt(data: Uint8Array): Uint8Array {
        return this.process(data);
    }

    public decrypt(data: Uint8Array): Uint8Array {
        return this.process(data);
    }
}
